import { Link } from "react-router-dom";
import {
  Users,
  DoorOpen,
  MusicNote,
  PaintBrush,
  BookOpenText,
  Waveform,
  Broadcast,
} from "@phosphor-icons/react";
import Layout from "@/components/Layout";
import { rooms } from "@/data/mockData";

const iconByCategory = {
  "ARTES VISUALES": PaintBrush,
  "MÚSICA": MusicNote,
  "LITERARIAS": BookOpenText,
  "ARTE DIGITAL": Waveform,
  "ESCÉNICAS": Broadcast,
  "MIXTO": DoorOpen,
};

export default function Rooms() {
  const now = new Date().toLocaleTimeString("es-ES", {
    hour: "2-digit",
    minute: "2-digit",
  });
  const totalPeople = rooms.reduce((acc, r) => acc + r.participants, 0);

  return (
    <Layout wide>
      <div data-testid="rooms-page">
        <header className="p-4 md:p-8 border-b border-[#27272A]">
          <div className="flex items-center gap-3 mb-2 font-mono text-[10px] tracking-[0.3em]">
            <span className="h-1.5 w-1.5 bg-[#00FF66] live-dot" />
            <span className="text-[#00FF66]">ABIERTO · 24/7</span>
            <span className="text-[#525252]">· AHORA {now}</span>
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-black tracking-tighter uppercase leading-none">
            Salas de
            <br />
            <span className="text-[#FF3E00]">trabajo</span>.
          </h1>
          <p className="font-mono text-sm text-[#A1A1AA] mt-5 max-w-2xl leading-relaxed">
            // CANALES PERMANENTES DONDE ARTISTAS TRABAJAN EN COMPAÑÍA. PINTA
            MIENTRAS OTROS PINTAN. ESCRIBE EN SILENCIO COMPARTIDO. JAM SIN
            INVITACIÓN. ENTRA Y SAL CUANDO QUIERAS.
          </p>

          <div className="mt-8 grid grid-cols-3 max-w-lg border border-[#27272A]">
            <StatCol n={rooms.length} label="SALAS ABIERTAS" />
            <StatCol n={totalPeople} label="EN CANAL AHORA" divider />
            <StatCol n="24/7" label="SIEMPRE ON" divider />
          </div>
        </header>

        <section className="p-4 md:p-6 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
          {rooms.map((room, i) => {
            const Icon = iconByCategory[room.category] || DoorOpen;
            const pct = Math.round((room.participants / room.capacity) * 100);
            return (
              <Link
                key={room.slug}
                to={`/salas/${room.slug}`}
                data-testid={`room-card-${room.slug}`}
                className="group relative block border border-[#27272A] hover:border-[#FF3E00] bg-[#0a0a0a] overflow-hidden transition-colors fade-up"
                style={{ animationDelay: `${i * 0.05}s` }}
              >
                {/* Ambient bg */}
                <div className="absolute inset-0 opacity-30 group-hover:opacity-50 transition-opacity">
                  <img
                    src={room.ambient}
                    alt=""
                    className="w-full h-full object-cover grayscale"
                  />
                  <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#050505]/60 to-transparent" />
                </div>

                <div className="relative p-5 min-h-[280px] flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <div
                      className="h-11 w-11 border inline-flex items-center justify-center"
                      style={{ borderColor: room.color, color: room.color }}
                    >
                      <Icon size={20} weight="fill" />
                    </div>
                    <span className="inline-flex items-center gap-1 font-mono text-[9px] uppercase tracking-[0.22em] bg-black/60 backdrop-blur-sm border border-[#00FF66]/40 text-[#00FF66] px-2 py-1">
                      <span className="h-1 w-1 bg-[#00FF66] live-dot" />
                      LIVE 24/7
                    </span>
                  </div>

                  <div className="mt-6">
                    <div
                      className="font-mono text-[10px] tracking-[0.22em] mb-1"
                      style={{ color: room.color }}
                    >
                      {room.category}
                    </div>
                    <h2 className="font-display font-black text-2xl md:text-3xl tracking-tighter uppercase leading-[1.05]">
                      {room.name}
                    </h2>
                    <div className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2 italic">
                      // {room.mood}
                    </div>
                  </div>

                  <p className="text-[13px] text-[#E5E5E5] leading-snug mt-4 flex-1">
                    {room.description}
                  </p>

                  {/* Footer: people + activity */}
                  <div className="mt-5 pt-4 border-t border-[#27272A] space-y-3">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center">
                        {room.members.slice(0, 4).map((slug, idx) => {
                          const a = (typeof slug === "object" ? slug : null) || null;
                          return null;
                        })}
                        <MembersStack slugs={room.members} />
                      </div>
                      <div className="font-mono text-[11px] tracking-wider inline-flex items-center gap-1.5">
                        <Users size={12} weight="fill" className="text-[#FF3E00]" />
                        <span className="text-white">{room.participants}</span>
                        <span className="text-[#525252]">/{room.capacity}</span>
                      </div>
                    </div>

                    {/* Capacity bar */}
                    <div className="h-0.5 bg-[#27272A]">
                      <div
                        className="h-full"
                        style={{
                          width: `${pct}%`,
                          background: room.color,
                        }}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-2 font-mono text-[10px]">
                      <span className="text-[#A1A1AA] tracking-wider truncate">
                        ▸ {room.activity}
                      </span>
                      <span className="inline-flex items-center gap-1 text-white bg-[#FF3E00] px-2 py-1 tracking-[0.2em] uppercase group-hover:bg-white transition-colors">
                        Entrar →
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </section>

        {/* CTA */}
        <section className="px-4 md:px-6 pb-10">
          <div className="border border-dashed border-[#27272A] p-6 md:p-8 bg-[#0a0a0a] flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="font-mono text-[10px] text-[#FF3E00] tracking-[0.3em] mb-2">
                ¿TIENES IDEA DE SALA?
              </div>
              <h3 className="font-display font-black text-2xl md:text-3xl uppercase tracking-tighter">
                Proponé una nueva sala.
              </h3>
              <p className="font-mono text-[11px] text-[#A1A1AA] tracking-wider mt-2 max-w-lg">
                LAS SALAS SON CURADAS POR EL EQUIPO EDITORIAL. MANDANOS TU
                IDEA: DISCIPLINA, FORMATO, CAPACIDAD.
              </p>
            </div>
            <button
              data-testid="propose-room-btn"
              className="btn-brutal font-mono uppercase text-xs tracking-[0.22em] bg-[#FF3E00] text-white px-6 py-3 font-bold"
            >
              + Proponer sala
            </button>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function StatCol({ n, label, divider }) {
  return (
    <div
      className={`p-4 text-center ${
        divider ? "border-l border-[#27272A]" : ""
      }`}
    >
      <div className="font-display font-black text-2xl md:text-3xl tracking-tight">
        {n}
      </div>
      <div className="font-mono text-[9px] text-[#A1A1AA] tracking-[0.22em] mt-1">
        {label}
      </div>
    </div>
  );
}

import { artists } from "@/data/mockData";
function MembersStack({ slugs }) {
  return (
    <div className="flex -space-x-2">
      {slugs.slice(0, 4).map((s) => {
        const a = artists.find((x) => x.slug === s);
        if (!a) return null;
        return (
          <img
            key={s}
            src={a.avatar}
            alt={a.name}
            title={a.name}
            className="h-7 w-7 object-cover border border-[#050505] grayscale"
          />
        );
      })}
      {slugs.length > 4 ? (
        <span className="h-7 min-w-[28px] px-1.5 bg-[#121212] border border-[#050505] font-mono text-[9px] flex items-center justify-center text-[#A1A1AA]">
          +{slugs.length - 4}
        </span>
      ) : null}
    </div>
  );
}
